import { MODULE_CATALOG, type ModuleKey, type Permission } from '@alicerce/contracts';
import { Alert, EmptyState, PageLoader } from '@alicerce/ui';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { safeNext } from '@/pages/auth/login';
import { useCurrent, useSession } from '@/lib/session';
import { LockedModule } from './locked-module';

/** Só entra logado; volta para a página pedida depois do login. */
export function RequireAuth() {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'loading') return <PageLoader />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search;
    return <Navigate to={`/entrar${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  return <Outlet />;
}

/** Só para quem não está logado (entrar, criar conta). Quem já entrou segue para o `?next=` (ex.: plano do site). */
export function RequireAnonymous() {
  const { status } = useSession();
  const [params] = useSearchParams();
  if (status === 'loading') return <PageLoader />;
  if (status === 'authenticated') return <Navigate to={safeNext(params.get('next'))} replace />;
  return <Outlet />;
}

/** Precisa de um negócio escolhido; quem ainda não tem vai para o cadastro do negócio. */
export function RequireTenant() {
  const { me, impersonating } = useSession();
  if (!me) return <PageLoader />;
  if (!impersonating && me.tenants.length === 0) return <Navigate to="/novo-negocio" replace />;
  if (!me.current) return <PageLoader />;
  return <Outlet />;
}

export function RequireStaff() {
  const { me } = useSession();
  if (!me) return <PageLoader />;
  if (!me.user.isPlatformStaff) return <Navigate to="/" replace />;
  return <Outlet />;
}

export function Forbidden() {
  return (
    <EmptyState
      title="Seu papel não permite abrir esta tela"
      description="Peça para quem é dono do negócio ajustar seu papel em Equipe."
    />
  );
}

/**
 * Tela de módulo: sem o módulo no plano, mostra o que ele faz, o preço e o botão de ativar (guia, 7.8);
 * em somente leitura, avisa no topo.
 */
export function ModuleRoute({ module, permission, children }: { module?: ModuleKey; permission?: Permission; children?: ReactNode }) {
  const { can, moduleStatus } = useCurrent();
  if (module) {
    const status = moduleStatus(module);
    if (status === 'DISABLED') return <LockedModule module={module} />;
    if (permission && !can(permission)) return <Forbidden />;
    return (
      <>
        {status === 'READ_ONLY' && (
          <Alert tone="warning" className="mb-4" title={`${MODULE_CATALOG[module].name} está em modo somente leitura.`}>
            Você pode consultar tudo, mas não alterar. Regularize a assinatura para voltar a editar.
          </Alert>
        )}
        {children ?? <Outlet />}
      </>
    );
  }
  if (permission && !can(permission)) return <Forbidden />;
  return <>{children ?? <Outlet />}</>;
}
